import {getPayerByNumber, Payer} from '@hconnect/apiclient'
import {Button, GenericInput} from '@hm/fundamental'
import {mdiMagnify} from '@mdi/js'
import {CircularProgress} from '@mui/material'
import React, {Reducer, useEffect, useReducer} from 'react'

import {api} from '../App.store'
import {useAdminPilotUser} from '../common/hooks/useAdminPilotUser'
import {usePayerWithoutNumber} from '../hooks/usePayer'
import {useTranslation} from '../hooks/useTranslation'
import {Permission} from '../modules/LoggedInUser'
import {GroupedRoleAssignment} from '../modules/ManageUsers.selectors'

import {CustomerOrPayerDropdown} from './Roles/CustomerOrPayerDropdown/deprecated/CustomerOrPayerDropdown'
import {
  initialTextFieldState,
  LookupState,
  TextFieldAction,
  textFieldReducer
} from './TextFieldReducer'

type RolePayerLookupTextFieldProps = {
  payers: Payer[]
  groupedRoleAssignment: GroupedRoleAssignment
  onChangePayerIds: (payerIds: string[]) => void
  dataScopeName: string
  setShowCustomerOrPayerDropdown: (show: boolean) => void
  name: string
  customerAdminData?: Record<string, string | undefined>
  loggedInUserPermissions?: Permission[] | null
  helperText?: string
  disabled?: boolean
}

// eslint-disable-next-line complexity
export const RolePayerLookupTextField = ({
  onChangePayerIds,
  payers,
  helperText,
  groupedRoleAssignment,
  dataScopeName,
  customerAdminData,
  setShowCustomerOrPayerDropdown,
  loggedInUserPermissions,
  ...props
}: RolePayerLookupTextFieldProps) => {
  const [state, dispatch] = useReducer<Reducer<LookupState, TextFieldAction>>(
    textFieldReducer,
    initialTextFieldState
  )
  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)

  const {
    variantDataScopes: {customersScopes}
  } = groupedRoleAssignment
  const {t} = useTranslation()

  const payerAdminQueryInfo = usePayerWithoutNumber(
    customerAdminData?.countryId || '',
    customerAdminData?.orgUnitId || '',
    customerAdminData?.businessLine || '',
    !!customerAdminData
  )

  useEffect(() => {
    payerAdminQueryInfo.data && setShowCustomerOrPayerDropdown(payerAdminQueryInfo.data.length > 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payerAdminQueryInfo.data])

  const handleLookup = async () => {
    const {text} = state
    if (text.trim().length === 0) {
      return
    }

    try {
      dispatch({type: 'LOOKUP_REQUEST'})
      const {countryId, orgUnitId, businessLine} = groupedRoleAssignment.commonDataScopes

      const countryIdArgument = typeof countryId === 'string' ? countryId : ''
      const orgUnitIdArgument = typeof orgUnitId === 'string' ? orgUnitId : ''
      const businessLineArgument = typeof businessLine === 'string' ? businessLine : ''

      const payers = await getPayerByNumber(api)(text, {
        countryId: countryIdArgument,
        orgUnitId: orgUnitIdArgument,
        businessLine: businessLineArgument
      })

      if (payers.length > 0) {
        dispatch({type: 'LOOKUP_SUCCESS'})

        const payerIds: string[] = payers.map((c: Payer) => c.payerId)

        onChangePayerIds(payerIds)
      } else {
        dispatch({
          type: 'LOOKUP_FAILURE',
          payload: new Error(t(`roleAssignment.noLookupResult.${dataScopeName}`)),
          error: true
        })
      }
    } catch (err) {
      dispatch({type: 'LOOKUP_FAILURE', payload: err as Error, error: true})
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    void handleLookup()
  }

  const handleAddPayer = (event: React.ChangeEvent<HTMLInputElement>) => {
    payerAdminQueryInfo.data &&
      payerAdminQueryInfo.data.length > 0 &&
      onChangePayerIds(
        payerAdminQueryInfo.data
          .filter((payer) => payer.payerId === event.target.value)
          .map((value) => value.payerId)
      )
    setShowCustomerOrPayerDropdown(false)
  }

  // don't allow to enter payers if customers exist
  const disabled = customersScopes.length > 0 || props.disabled
  const hidden =
    !payerAdminQueryInfo.isFetching &&
    isUserAdminPilot &&
    (!payerAdminQueryInfo.data || payerAdminQueryInfo.data.length === 0)

  if (payerAdminQueryInfo.isFetching) {
    return <CircularProgress style={{margin: '8px 0'}} />
  }

  if (hidden) {
    return null
  }

  return (
    <>
      {!isUserAdminPilot ? (
        <div className="flex gap-2 w-full max-w-[600px]">
          <GenericInput
            {...props}
            label={t(`roleAssignment.dataScope.${dataScopeName}`)}
            value={disabled ? '' : state.text}
            onChange={(value) => dispatch({type: 'CHANGE_TEXT', payload: value})}
            onKeyDown={handleKeyDown}
            error={state.errorMessage || helperText}
            type="search"
            isDisabled={disabled}
            isReadOnly={state.isLoading}
            isTouched={true}
            className="flex-1"
          />
          <Button
            data-test-id="button-payer-lookup"
            variant="secondary"
            icon={state.isLoading ? <CircularProgress size={16} /> : mdiMagnify}
            type="button"
            className="h-auto"
            onClick={handleLookup}
            disabled={disabled || !state.text}
          >
            Verify
          </Button>
        </div>
      ) : (
        <CustomerOrPayerDropdown
          dataScope="payerIds"
          name="payerSelect"
          onChange={handleAddPayer}
          disabled={disabled}
          options={
            payerAdminQueryInfo.data?.map((data) => ({
              label: data.payerNumber || '',
              subLabel: data.payerName || '',
              value: data.payerId || ''
            })) || []
          }
          loggedInUserPermissions={loggedInUserPermissions}
        />
      )}
    </>
  )
}
